import React, { useState, useCallback, useRef } from "react";
import axios from "axios";
import confirmAction from "../../utils/confirm";

const csrfToken = () => document.querySelector("[name=csrf-token]")?.content;

const axiosConfig = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken() },
});

const axiosMultipart = () => ({
  headers: { "X-CSRF-TOKEN": csrfToken(), "Content-Type": "multipart/form-data" },
});

const CAPTION_POSITIONS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
];

const POSITION_PRESETS = [
  { label: "Top", x: 50, y: 0 },
  { label: "Center", x: 50, y: 50 },
  { label: "Bottom", x: 50, y: 100 },
  { label: "Left", x: 0, y: 50 },
  { label: "Right", x: 100, y: 50 },
];

const clampPercent = (value) => Math.max(0, Math.min(100, value));
const clampZoom = (value) => Math.max(100, Math.min(200, value));

const valueOrDefault = (value, fallback) => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

const backgroundSizeForZoom = (zoom) => (Number(zoom) === 100 ? "cover" : `${zoom}% auto`);

const SlideEditor = ({ slide, locales, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, locale }) => {
  const [title, setTitle] = useState(slide.title || {});
  const [subtitle, setSubtitle] = useState(slide.subtitle || {});
  const [buttonText, setButtonText] = useState(slide.button_text || {});
  const [buttonLink, setButtonLink] = useState(slide.button_link || "");
  const [captionPosition, setCaptionPosition] = useState(slide.caption_position || "left");
  const [titleFontSize, setTitleFontSize] = useState(slide.title_font_size || "");
  const [subtitleFontSize, setSubtitleFontSize] = useState(slide.subtitle_font_size || "");
  const [overlay, setOverlay] = useState(slide.overlay || false);
  const [image, setImage] = useState(slide.image);
  const [backgroundPositionX, setBackgroundPositionX] = useState(valueOrDefault(slide.background_position_x, 50));
  const [backgroundPositionY, setBackgroundPositionY] = useState(valueOrDefault(slide.background_position_y, 50));
  const [backgroundZoom, setBackgroundZoom] = useState(valueOrDefault(slide.background_zoom, 100));
  const [draggingPosition, setDraggingPosition] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeLocale, setActiveLocale] = useState(locales[0]);
  const previewRef = useRef(null);

  const updateJsonField = (setter, current, loc, value) => {
    setter({ ...current, [loc]: value });
  };

  const setBackgroundPosition = (x, y) => {
    setBackgroundPositionX(clampPercent(Math.round(x)));
    setBackgroundPositionY(clampPercent(Math.round(y)));
  };

  const updatePositionFromPointer = (event) => {
    const preview = previewRef.current;
    if (!preview) return;

    const pointer = event.touches ? event.touches[0] : event;
    const rect = preview.getBoundingClientRect();
    const x = ((pointer.clientX - rect.left) / rect.width) * 100;
    const y = ((pointer.clientY - rect.top) / rect.height) * 100;

    setBackgroundPosition(x, y);
  };

  const handlePositionStart = (event) => {
    setDraggingPosition(true);
    updatePositionFromPointer(event);
  };

  const handlePositionMove = (event) => {
    if (!draggingPosition) return;
    updatePositionFromPointer(event);
  };

  const nudgePosition = (deltaX, deltaY) => {
    setBackgroundPosition(
      Number(backgroundPositionX) + deltaX,
      Number(backgroundPositionY) + deltaY
    );
  };

  const updateZoom = (delta) => {
    setBackgroundZoom(clampZoom(Number(backgroundZoom) + delta));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        title, subtitle, button_text: buttonText,
        button_link: buttonLink, caption_position: captionPosition,
        title_font_size: titleFontSize, subtitle_font_size: subtitleFontSize,
        overlay,
        background_position_x: backgroundPositionX,
        background_position_y: backgroundPositionY,
        background_zoom: backgroundZoom,
      };
      const res = await axios.patch(`/${locale}/admin/home_slider/slides/${slide.id}`, data, axiosConfig());
      onUpdate(res.data);
    } catch (e) {
      console.error("Failed to save slide:", e);
    }
    setSaving(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("image", file);
    // Also send current fields so update doesn't blank them
    Object.entries(title).forEach(([k, v]) => formData.append(`title[${k}]`, v));
    Object.entries(subtitle).forEach(([k, v]) => formData.append(`subtitle[${k}]`, v));
    Object.entries(buttonText).forEach(([k, v]) => formData.append(`button_text[${k}]`, v));
    formData.append("button_link", buttonLink);
    formData.append("caption_position", captionPosition);
    formData.append("title_font_size", titleFontSize);
    formData.append("subtitle_font_size", subtitleFontSize);
    formData.append("overlay", overlay);
    formData.append("background_position_x", backgroundPositionX);
    formData.append("background_position_y", backgroundPositionY);
    formData.append("background_zoom", backgroundZoom);
    try {
      const res = await axios.patch(`/${locale}/admin/home_slider/slides/${slide.id}`, formData, axiosMultipart());
      setImage(res.data.image);
      onUpdate(res.data);
    } catch (e) {
      console.error("Failed to upload image:", e);
    }
    e.target.value = "";
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="card-title mb-0">Slide #{slide.position + 1}</h5>
          <div>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isFirst} onClick={onMoveUp}>&#9650;</button>
            <button className="btn btn-sm btn-outline-secondary mr-1" disabled={isLast} onClick={onMoveDown}>&#9660;</button>
            <button className="btn btn-sm btn-danger" onClick={() => confirmAction(() => onDelete(slide.id))}>Delete</button>
          </div>
        </div>

        {/* Image preview + upload */}
        <div className="form-group">
          <label>Background Image</label>
          {image && (
            <div className="mb-2">
              <div
                ref={previewRef}
                className="rounded border position-relative"
                role="button"
                tabIndex={0}
                onMouseDown={handlePositionStart}
                onMouseMove={handlePositionMove}
                onMouseUp={() => setDraggingPosition(false)}
                onMouseLeave={() => setDraggingPosition(false)}
                onTouchStart={handlePositionStart}
                onTouchMove={handlePositionMove}
                onTouchEnd={() => setDraggingPosition(false)}
                style={{
                  backgroundImage: `url(${image.url})`,
                  backgroundPosition: `${backgroundPositionX}% ${backgroundPositionY}%`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: backgroundSizeForZoom(backgroundZoom),
                  cursor: "crosshair",
                  height: 180,
                  maxWidth: 520,
                  overflow: "hidden",
                }}
                title="Click or drag to choose which part of the image stays visible"
              >
                <span
                  className="position-absolute bg-primary rounded-circle"
                  style={{
                    height: 14,
                    left: `${backgroundPositionX}%`,
                    top: `${backgroundPositionY}%`,
                    transform: "translate(-50%, -50%)",
                    width: 14,
                  }}
                />
              </div>
              <small className="form-text text-muted">
                Click or drag the marker to choose the image focal point. Current position: {backgroundPositionX}% {backgroundPositionY}%.
              </small>
              <div className="mt-2">
                <span className="mr-2">Zoom: {backgroundZoom}%</span>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => updateZoom(-5)}>-</button>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => updateZoom(5)}>+</button>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => setBackgroundZoom(100)}>Reset</button>
              </div>
              <div className="mt-2">
                {POSITION_PRESETS.map((preset) => (
                  <button
                    key={preset.label}
                    type="button"
                    className="btn btn-sm btn-outline-secondary mr-1 mb-1"
                    onClick={() => setBackgroundPosition(preset.x, preset.y)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="mt-1">
                <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => nudgePosition(-5, 0)}>&lt;</button>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => nudgePosition(5, 0)}>&gt;</button>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => nudgePosition(0, -5)}>&#8593;</button>
                <button type="button" className="btn btn-sm btn-outline-secondary mr-1" onClick={() => nudgePosition(0, 5)}>&#8595;</button>
              </div>
            </div>
          )}
          <input type="file" className="form-control-file" accept="image/*" onChange={handleImageUpload} />
        </div>

        {/* Locale tabs */}
        <ul className="nav nav-tabs mt-3">
          {locales.map((loc) => (
            <li className="nav-item" key={loc}>
              <a
                className={`nav-link ${activeLocale === loc ? "active" : ""}`}
                href="#"
                onClick={(e) => { e.preventDefault(); setActiveLocale(loc); }}
              >
                {loc.toUpperCase()}
              </a>
            </li>
          ))}
        </ul>
        <div className="border border-top-0 p-3 mb-3">
          <div className="form-group">
            <label>Title ({activeLocale.toUpperCase()})</label>
            <input
              type="text" className="form-control"
              value={title[activeLocale] || ""}
              onChange={(e) => updateJsonField(setTitle, title, activeLocale, e.target.value)}
            />
          </div>
          <div className="form-group mt-2">
            <label>Subtitle ({activeLocale.toUpperCase()})</label>
            <input
              type="text" className="form-control"
              value={subtitle[activeLocale] || ""}
              onChange={(e) => updateJsonField(setSubtitle, subtitle, activeLocale, e.target.value)}
            />
          </div>
          <div className="form-group mt-2">
            <label>Button Text ({activeLocale.toUpperCase()})</label>
            <input
              type="text" className="form-control"
              value={buttonText[activeLocale] || ""}
              onChange={(e) => updateJsonField(setButtonText, buttonText, activeLocale, e.target.value)}
            />
          </div>
        </div>

        {/* Scalar fields */}
        <div className="row">
          <div className="col-md-6 form-group">
            <label>Button Link</label>
            <input type="text" className="form-control" value={buttonLink} onChange={(e) => setButtonLink(e.target.value)} />
          </div>
          <div className="col-md-6 form-group">
            <label>Caption Position</label>
            <select className="form-control" value={captionPosition} onChange={(e) => setCaptionPosition(e.target.value)}>
              {CAPTION_POSITIONS.map((p) => (
                <option key={p.value} value={p.value}>{p.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 form-group">
            <label>Title Font Size</label>
            <input type="text" className="form-control" placeholder="e.g. 48px" value={titleFontSize} onChange={(e) => setTitleFontSize(e.target.value)} />
          </div>
          <div className="col-md-4 form-group">
            <label>Subtitle Font Size</label>
            <input type="text" className="form-control" placeholder="e.g. 24px" value={subtitleFontSize} onChange={(e) => setSubtitleFontSize(e.target.value)} />
          </div>
          <div className="col-md-4 form-group d-flex align-items-end">
            <div className="form-check">
              <input type="checkbox" className="form-check-input" id={`overlay-${slide.id}`} checked={overlay} onChange={(e) => setOverlay(e.target.checked)} />
              <label className="form-check-label" htmlFor={`overlay-${slide.id}`}>Dark Overlay</label>
            </div>
          </div>
        </div>

        <button className="btn btn-primary mt-2" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Slide"}
        </button>
      </div>
    </div>
  );
};

const HomeSliderManager = ({ initialSlides, locale, locales }) => {
  const [slides, setSlides] = useState(initialSlides || []);
  const [adding, setAdding] = useState(false);

  const reorderSlides = useCallback(async (newSlides) => {
    setSlides(newSlides);
    const ids = newSlides.map((s) => s.id);
    try {
      await axios.patch(`/${locale}/admin/home_slider/slides/reorder`, { ids }, axiosConfig());
    } catch (e) {
      console.error("Failed to reorder slides:", e);
    }
  }, [locale]);

  const moveItem = (list, index, direction) => {
    const newList = [...list];
    const swapIndex = index + direction;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    reorderSlides(newList);
  };

  const handleAdd = async () => {
    setAdding(true);
    try {
      const data = {
        title: {}, subtitle: {}, button_text: {},
        button_link: "", caption_position: "left", overlay: false,
        background_position_x: 50, background_position_y: 50, background_zoom: 100,
      };
      const res = await axios.post(`/${locale}/admin/home_slider/slides`, data, axiosConfig());
      setSlides([...slides, res.data]);
    } catch (e) {
      console.error("Failed to create slide:", e);
    }
    setAdding(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/${locale}/admin/home_slider/slides/${id}`, axiosConfig());
      setSlides(slides.filter((s) => s.id !== id));
    } catch (e) {
      console.error("Failed to delete slide:", e);
    }
  };

  const handleUpdate = (updatedSlide) => {
    setSlides(slides.map((s) => (s.id === updatedSlide.id ? { ...s, ...updatedSlide } : s)));
  };

  const refetchSlides = async () => {
    try {
      const res = await axios.get(`/${locale}/admin/home_slider.json`, axiosConfig());
      setSlides(res.data.slides);
    } catch (e) {
      console.error("Failed to fetch slides:", e);
    }
  };

  return (
    <div className="mt-3">
      {slides.map((slide, index) => (
        <SlideEditor
          key={slide.id}
          slide={slide}
          locales={locales}
          locale={locale}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
          onMoveUp={() => moveItem(slides, index, -1)}
          onMoveDown={() => moveItem(slides, index, 1)}
          isFirst={index === 0}
          isLast={index === slides.length - 1}
        />
      ))}

      <div className="card mb-4">
        <div className="card-body text-center">
          <button className="btn btn-success" onClick={handleAdd} disabled={adding}>
            {adding ? "Adding..." : "Add New Slide"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HomeSliderManager;
